/**
 * Consentimento de cookies (LGPD) + Google Consent Mode v2.
 *
 * O padrão "negado" NÃO mora aqui: ele é declarado num <script> inline no
 * <head> do index.html, que roda antes de qualquer coisa — inclusive antes do
 * GTM, que é carregado 2 s depois do `load`. O mesmo script relê a escolha
 * salva e já manda o `update`, para quem volta ao site não ter nenhuma tag
 * esperando o React hidratar. Este módulo cuida do resto: ler/gravar a escolha
 * e avisar o GTM quando ela muda.
 *
 * A chave e o formato são contrato com aquele script. Mudou um, mude o outro.
 *
 * Nada aqui roda no topo do módulo: o arquivo é importado por componentes que
 * também são prerenderizados no Node (ver docs/SSG.md).
 */

export const CHAVE_CONSENTIMENTO = 'habitus_consentimento_v1';

/** Evento que o rodapé dispara para reabrir o banner em "Configurar". */
export const EVENTO_ABRIR_PREFERENCIAS = 'habitus:abrir-preferencias-cookies';

export interface Consentimento {
  analise: boolean;
  publicidade: boolean;
  /** ISO 8601 do momento da escolha — registro de quando o aceite foi dado. */
  em: string;
}

type Estado = 'granted' | 'denied';

export function lerConsentimento(): Consentimento | null {
  try {
    const bruto = localStorage.getItem(CHAVE_CONSENTIMENTO);
    if (!bruto) return null;
    const c = JSON.parse(bruto);
    if (typeof c?.analise !== 'boolean' || typeof c?.publicidade !== 'boolean') return null;
    return c as Consentimento;
  } catch {
    return null;
  }
}

/** Há aceite de publicidade agora? Sem escolha registrada, a resposta é não. */
export function temConsentimentoPublicidade(): boolean {
  return lerConsentimento()?.publicidade === true;
}

function sinais(c: Pick<Consentimento, 'analise' | 'publicidade'>): Record<string, Estado> {
  const pub: Estado = c.publicidade ? 'granted' : 'denied';
  return {
    analytics_storage: c.analise ? 'granted' : 'denied',
    ad_storage: pub,
    ad_user_data: pub,
    ad_personalization: pub,
  };
}

/**
 * Mesmo formato do `gtag()`: o GTM só reconhece comando de consentimento
 * empilhado como o objeto `arguments`, não como array comum.
 * (`window.dataLayer` é declarado em src/hooks/useTrackEvent.ts.)
 */
function gtag(..._args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments as unknown as Record<string, unknown>);
}

/**
 * Grava a escolha e atualiza o Consent Mode.
 *
 * Retirar um aceite que já tinha sido dado recarrega a página: o pixel do Meta
 * e o gtag já carregados não "descarregam" com um `update`, e só a página nova
 * nasce sem eles.
 */
export function salvarConsentimento(escolha: Pick<Consentimento, 'analise' | 'publicidade'>): void {
  const anterior = lerConsentimento();
  const registro: Consentimento = { ...escolha, em: new Date().toISOString() };
  try {
    localStorage.setItem(CHAVE_CONSENTIMENTO, JSON.stringify(registro));
  } catch {
    /* storage bloqueado: vale só para esta página */
  }

  gtag('consent', 'update', sinais(escolha));
  window.dataLayer!.push({
    event: 'consentimento_atualizado',
    consentimento_analise: escolha.analise,
    consentimento_publicidade: escolha.publicidade,
  });

  const retirou =
    (anterior?.analise && !escolha.analise) || (anterior?.publicidade && !escolha.publicidade);
  if (retirou) window.location.reload();
}

export function abrirPreferenciasDeCookies(): void {
  window.dispatchEvent(new Event(EVENTO_ABRIR_PREFERENCIAS));
}
